/**
 * Pure schema-control patch — single place for path write + freeze coupling
 * + type-family coercion. ExhibitApp.patchControl is the runtime entry.
 */

import {
  cloneAudioParams,
  validateAudioParams,
  type AudioParams,
} from "../audio/params";
import { assignPathClone, pathRoot } from "../help/pathAccess";
import { applyFreezeTopology, applyRechooseProb } from "../sim/freezeCoupling";
import {
  cloneRenderParams,
  cloneSimParams,
  type RenderParams,
  type SimParams,
} from "../sim/params";
import { coerceTypeFamilyParams } from "../sim/typeRules";

export type ControlPatchResult =
  | { kind: "sim"; params: SimParams }
  | { kind: "render"; render: RenderParams }
  | { kind: "audio"; audio: AudioParams }
  | { kind: "noop" };

/**
 * Apply one schema control edit to cloned bags.
 * Path: `sim.*` | `render.*` | `audio.*`.
 */
export function applyControlPatch(
  sim: SimParams,
  render: RenderParams,
  audio: AudioParams,
  path: string,
  value: unknown,
): ControlPatchResult {
  const root = pathRoot(path);
  if (root === "sim") {
    let next = assignPathClone(
      cloneSimParams(sim) as unknown as Record<string, unknown>,
      path,
      value,
    ) as unknown as SimParams;
    if (path === "sim.rechooseProb") {
      next = applyRechooseProb(next, Number(value));
    } else if (path === "sim.freezeTopology") {
      next = applyFreezeTopology(next, Boolean(value));
    } else if (path === "sim.typeCount") {
      next = coerceTypeFamilyParams(next, Number(value));
    }
    return { kind: "sim", params: next };
  }
  if (root === "render") {
    const next = assignPathClone(
      cloneRenderParams(render) as unknown as Record<string, unknown>,
      path,
      value,
    ) as unknown as RenderParams;
    return { kind: "render", render: next };
  }
  if (root === "audio") {
    const next = assignPathClone(
      cloneAudioParams(audio) as unknown as Record<string, unknown>,
      path,
      value,
    ) as unknown as AudioParams;
    return { kind: "audio", audio: validateAudioParams(next) };
  }
  return { kind: "noop" };
}
